import React, { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { Camera, RefreshCw, Upload, X } from 'lucide-react';
import api, { getAssetUrl, getApiErrorMessage } from '../../services/api';

const initialFormState = {
  quem_retirou: '',
  documento_quem_retirou: '',
};

const RetiradaModal = ({ entrega, isOpen, onClose, onSuccess, moradores }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [image, setImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [selectedMorador, setSelectedMorador] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewFrameRef = useRef(null);

  const isSecureContextForCamera =
    typeof window !== 'undefined' &&
    (window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormData({
      quem_retirou: entrega?.quem_retirou || '',
      documento_quem_retirou: entrega?.documento_quem_retirou || '',
    });
    setSelectedFile(null);
    setSelectedMorador(null);
    setShowSuggestions(false);
    setImage(getAssetUrl(entrega?.foto_retirada_url));
    setCameraOpen(false);
    setCameraError('');
  }, [entrega, isOpen]);

  useEffect(() => {
    return () => {
      if (image?.startsWith?.('blob:')) {
        URL.revokeObjectURL(image);
      }
    };
  }, [image]);

  if (!isOpen || !entrega) {
    return null;
  }

  const handleClose = () => {
    if (image?.startsWith?.('blob:')) {
      URL.revokeObjectURL(image);
    }

    setImage(null);
    setSelectedFile(null);
    setSelectedMorador(null);
    setShowSuggestions(false);
    setCameraOpen(false);
    setCameraError('');
    setFormData(initialFormState);
    onClose();
  };

  const termoMorador = formData.quem_retirou.trim().toLowerCase();
  const sugestoesMoradores = termoMorador
    ? moradores
        .filter((morador) => (morador.nome || '').toLowerCase().includes(termoMorador))
        .slice(0, 6)
    : [];

  const selecionarMorador = (morador) => {
    setSelectedMorador(morador);
    setFormData((current) => ({
      ...current,
      quem_retirou: morador.nome || '',
      documento_quem_retirou: current.documento_quem_retirou || morador.cpf || '',
    }));
    setShowSuggestions(false);
  };

  const handleCameraError = (error) => {
    const errorName = error?.name || '';

    if (!isSecureContextForCamera) {
      setCameraError('A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.');
      return;
    }

    if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
      setCameraError('O navegador bloqueou a camera. Libere a permissao da camera no cadeado do site.');
      return;
    }

    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
      setCameraError('Nenhuma camera foi encontrada neste computador.');
      return;
    }

    if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
      setCameraError('A camera esta ocupada por outro programa, como Zoom, Meet ou Teams.');
      return;
    }

    setCameraError('Nao foi possivel abrir a camera neste navegador. Envie uma imagem do computador.');
  };

  const handleOpenCamera = () => {
    setCameraError('');

    if (!isSecureContextForCamera) {
      setCameraOpen(false);
      setCameraError('A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.');
      return;
    }

    if (image?.startsWith?.('blob:')) {
      URL.revokeObjectURL(image);
    }

    setImage(null);
    setSelectedFile(null);
    setCameraOpen(true);
  };

  const capture = () => {
    const video = webcamRef.current?.video;
    const frame = previewFrameRef.current;

    if (!video || !frame || !video.videoWidth || !video.videoHeight) {
      setCameraError('Nao foi possivel capturar a imagem da camera.');
      return;
    }

    const frameWidth = frame.clientWidth || video.videoWidth;
    const frameHeight = frame.clientHeight || video.videoHeight;
    const targetAspectRatio = frameWidth / frameHeight;
    const sourceAspectRatio = video.videoWidth / video.videoHeight;

    let sourceWidth = video.videoWidth;
    let sourceHeight = video.videoHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (sourceAspectRatio > targetAspectRatio) {
      sourceWidth = video.videoHeight * targetAspectRatio;
      sourceX = (video.videoWidth - sourceWidth) / 2;
    } else {
      sourceHeight = video.videoWidth / targetAspectRatio;
      sourceY = (video.videoHeight - sourceHeight) / 2;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sourceWidth);
    canvas.height = Math.round(sourceHeight);
    const context = canvas.getContext('2d');

    if (!context) {
      setCameraError('Nao foi possivel capturar a imagem da camera.');
      return;
    }

    context.drawImage(
      video,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    setImage(canvas.toDataURL('image/jpeg', 0.92));
    setSelectedFile(null);
    setCameraOpen(false);
    setCameraError('');
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (image?.startsWith?.('blob:')) {
      URL.revokeObjectURL(image);
    }

    setSelectedFile(file);
    setImage(URL.createObjectURL(file));
    setCameraOpen(false);
    setCameraError('');
  };

  const handleRemovePhoto = () => {
    if (image?.startsWith?.('blob:')) {
      URL.revokeObjectURL(image);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setImage(null);
    setSelectedFile(null);
    setCameraOpen(false);
    setCameraError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = new FormData();
    payload.append('quem_retirou', formData.quem_retirou.trim());
    payload.append('documento_quem_retirou', formData.documento_quem_retirou.trim());
    if (selectedMorador?.id) {
      payload.append('morador_retirada_id', selectedMorador.id);
    }

    if (selectedFile) {
      payload.append('foto', selectedFile, selectedFile.name);
    } else if (image && !/^https?:\/\//i.test(image)) {
      const response = await fetch(image);
      const blob = await response.blob();
      payload.append('foto', blob, 'retirada.jpg');
    }

    try {
      const response = await api.patch(`/entregas/${entrega.id}/retirada`, payload);
      handleClose();
      onSuccess(response.data.entrega);
    } catch (error) {
      alert(getApiErrorMessage(error, 'Erro ao dar baixa na encomenda.'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-4 md:px-5">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Baixa da encomenda</h2>
            <p className="text-sm text-slate-500">
              {entrega.nome_morador} • Unidade {entrega.unidade}
            </p>
          </div>
          <button onClick={handleClose} type="button" className="text-gray-400 hover:text-red-500">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 md:px-5 md:py-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
            <div className="space-y-4">
              <div
                ref={previewFrameRef}
                className="relative h-[220px] overflow-hidden rounded-[28px] border border-slate-700/20 bg-slate-950 shadow-sm sm:h-[240px] lg:h-[320px]"
              >
                {!image ? (
                  <>
                    {cameraOpen && !cameraError ? (
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ facingMode: 'user' }}
                        className="h-full w-full object-cover"
                        onUserMediaError={handleCameraError}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-center text-slate-300">
                        <div className="space-y-3 px-6">
                          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-white/15 bg-white/5">
                            <Camera size={26} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-white">
                              {cameraError || 'Clique em capturar imagem para abrir a camera ou envie uma foto do computador.'}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              A foto da retirada e opcional.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <img src={image} alt="Foto da retirada" className="h-full w-full object-cover" />
                )}
              </div>

              {cameraError && !cameraOpen && (
                <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  {cameraError}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                {!cameraOpen && !image && (
                  <button
                    type="button"
                    onClick={handleOpenCamera}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    <Camera size={18} />
                    Capturar imagem
                  </button>
                )}

                {cameraOpen && (
                  <>
                    <button
                      type="button"
                      onClick={capture}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      <Camera size={18} />
                      Tirar foto
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCameraOpen(false);
                        setCameraError('');
                      }}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <X size={18} />
                      Fechar camera
                    </button>
                  </>
                )}

                {image && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                  >
                    <RefreshCw size={18} />
                    Limpar foto
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <Upload size={18} />
                  Enviar foto
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <div className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Nome de quem retirou</span>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.quem_retirou}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSelectedMorador(null);
                      setFormData((current) => ({ ...current, quem_retirou: value }));
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition-all focus:ring-2 focus:ring-condo-teal"
                    placeholder="Opcional"
                    autoComplete="off"
                  />
                  {showSuggestions && sugestoesMoradores.length > 0 && (
                    <div className="absolute z-20 mt-2 max-h-56 w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-xl">
                      {sugestoesMoradores.map((morador) => (
                        <button
                          key={morador.id}
                          type="button"
                          onClick={() => selecionarMorador(morador)}
                          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-slate-50"
                        >
                          {morador.foto_url ? (
                            <img
                              src={getAssetUrl(morador.foto_url)}
                              alt={morador.nome}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                              <Camera size={16} />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-semibold text-slate-800">{morador.nome}</div>
                            <div className="text-xs text-slate-400">Unidade {morador.unidade || '-'}{morador.cpf ? ` • ${morador.cpf}` : ''}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Numero do documento</span>
                <input
                  type="text"
                  value={formData.documento_quem_retirou}
                  onChange={(event) => setFormData((current) => ({ ...current, documento_quem_retirou: event.target.value }))}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition-all focus:ring-2 focus:ring-condo-teal"
                  placeholder="Opcional"
                />
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-xl border border-gray-200 px-4 py-2.5 font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="rounded-xl bg-slate-900 px-5 py-2.5 font-semibold text-white transition hover:bg-slate-800"
            >
              Salvar baixa
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RetiradaModal;
